import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  LinearProgress,
  IconButton,
  Autocomplete,
  TextField
} from '@mui/material';
import { Close } from '@mui/icons-material';
import equipmentService from '../../services/equipmentService';
import circuitService, { Circuito } from '../../services/circuitService';
import { contractService } from '../../services/contractService';

interface VincularCircuitoModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  equipment: any | null;
}

export type CircuitoOptionType = Circuito | { inputValue: string; identificacao: number; label: string; isNew: boolean } | string;

const VincularCircuitoModal: React.FC<VincularCircuitoModalProps> = ({
  open,
  onClose,
  onSuccess,
  equipment,
}) => {
  const [circuitos, setCircuitos] = useState<Circuito[]>([]);
  const [selectedCircuito, setSelectedCircuito] = useState<CircuitoOptionType | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingCircuitos, setLoadingCircuitos] = useState(false);
  const [error, setError] = useState<string | null>(null);


  useEffect(() => {
    if (open && equipment?.caixa_id) {
      if (equipment.circuito_id) {
        // Will be matched when circuitos are loaded
      } else {
        setSelectedCircuito(null);
      }
      loadCircuitos(equipment.caixa_id);
    } else {
      setCircuitos([]);
      setSelectedCircuito(null);
      setError(null);
    }
  }, [open, equipment]);

  useEffect(() => {
    if (equipment?.circuito_id && circuitos.length > 0) {
      const circ = circuitos.find(c => c.id === equipment.circuito_id);
      if (circ) {
        setSelectedCircuito(circ);
      }
    }
  }, [circuitos, equipment]);

  const loadCircuitos = async (caixaId: number) => {
    try {
      setLoadingCircuitos(true);
      setError(null);

      const response = await circuitService.getCircuitos({
        caixa_id: caixaId,
        situacao: 'livre',
        limit: 100
      });

      setCircuitos(response.circuitos || []);

    } catch (err: any) {
      console.error('Erro ao carregar circuitos:', err);
      setError(`Erro ao carregar circuitos: ${err.message}`);
      setCircuitos([]);
    } finally {
      setLoadingCircuitos(false);
    }
  };

  const handleSave = async () => {
    if (!equipment) return;

    try {
      setLoading(true);
      setError(null);

      let finalCircuitoId: number | null = null;

      if (selectedCircuito && typeof selectedCircuito === 'string') {
        // Verificar se esse identificador já existe na lista de circuitos (livres)
        const existing = circuitos.find(c => String(c.identificacao) === selectedCircuito);

        if (existing) {
          finalCircuitoId = existing.id;
        } else {
          // Obter endereco do contrato
          let endereco = 'Endereço não informado';
          if (equipment.contrato_id) {
            const contractRes = await contractService.getById(equipment.contrato_id);
            if (contractRes.success && contractRes.data?.endereco_inst) {
              endereco = contractRes.data.endereco_inst + ' - ' + contractRes.data.bairro_inst;
            }
          }

          // Criar o novo circuito
          const newCircuito = await circuitService.createCircuit({
            identificacao: selectedCircuito,
            caixa_id: equipment.caixa_id,
            endereco: endereco,
            situacao: 'ocupado',
            contrato_id: equipment.contrato_id || undefined
          });
          finalCircuitoId = newCircuito.circuit.id;
        }
      } else if (selectedCircuito && typeof selectedCircuito === 'object' && 'id' in selectedCircuito && selectedCircuito.id !== undefined) {
        finalCircuitoId = selectedCircuito.id;
      }

      if (finalCircuitoId === null) {
        throw new Error('Não foi possível obter um ID de circuito válido.');
      } const updateData: any = {
        circuito_id: finalCircuitoId
      };

      await equipmentService.updateEquipment(equipment.id, updateData);

      onSuccess();
    } catch (err: any) {
      console.error('Erro ao vincular circuito:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Erro ao vincular circuito ao equipamento';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ p: 1.5 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
            Vincular Circuito
          </Typography>
          <IconButton onClick={handleClose} size="small" sx={{ p: 0.5 }}>
            <Close sx={{ fontSize: '1rem' }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 1.5 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 1.5, fontSize: '0.8rem' }}>
            {error}
          </Alert>
        )}

        {loading && <LinearProgress sx={{ mb: 1.5 }} />}

        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Selecione um circuito livre na caixa <strong>{equipment?.caixa?.name || 'N/A'}</strong> para vincular ao equipamento <strong>{equipment?.modelo?.titulo || 'N/A'}</strong> (Série: {equipment?.numero_serie}).
          </Typography>

          <FormControl fullWidth disabled={loadingCircuitos || loading} size="small">
            <Autocomplete
              freeSolo
              size="small"
              options={circuitos}
              getOptionLabel={(option) => typeof option === 'string' ? option : String((option as Circuito).identificacao)}
              disabled={loadingCircuitos || loading}
              value={selectedCircuito as any}
              onChange={(_, newValue) => setSelectedCircuito(newValue)}
              onInputChange={(_, newInputValue) => {
                if (!circuitos.find(c => String(c.identificacao) === newInputValue)) {
                  setSelectedCircuito(newInputValue);
                }
                setInputValue(newInputValue);
              }}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props as any;
                const isString = typeof option === 'string';
                const label = isString ? option : String((option as Circuito).identificacao);
                const end = !isString ? (option as Circuito).endereco : '';
                return (
                  <li {...optionProps} key={key || (isString ? option : (option as Circuito).id)}>
                    <Box>
                      <Typography variant="body2">{label}</Typography>
                      {end && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -0.5 }}>
                          {end}
                        </Typography>
                      )}
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Selecione ou digite o Circuito"
                  placeholder={loadingCircuitos ? "Carregando..." : "Selecione um na lista ou digite um novo..."}
                />
              )}
            />
          </FormControl>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 1.5 }}>
        <Button onClick={handleClose} disabled={loading} size="small">
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          disabled={loading}
          variant="contained"
          color="primary"
          size="small"
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VincularCircuitoModal;
